import { useQueries, useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { toTxn, type Txn } from './transactionWire'

export class TransactionError extends Error {
  readonly status: number | undefined
  constructor(cause: unknown, status?: number) {
    super('transaction-unavailable', { cause })
    this.name = 'TransactionError'
    this.status = status
  }
}

const isMissing = (e: unknown) => e instanceof TransactionError && e.status === 404

export async function getTransaction(id: string): Promise<Txn> {
  const { data, error, response } = await api.GET('/api/v1/transactions/{transaction_id}', {
    params: { path: { transaction_id: id } },
  })
  if (error !== undefined || data === undefined) throw new TransactionError(error, response.status)
  return toTxn(data)
}

/**
 * One transaction. `missing` is the 404: the bank removed it, or a pending row
 * was replaced by its posted one. That is a fact about this row, not a failure
 * to retry, so it gets its own words and a way out instead of "Try again".
 */
export function useTransaction(id: string | undefined): AsyncState<Txn> & { missing: boolean } {
  const query = useQuery({
    queryKey: keys.transactions.detail(id ?? ''),
    queryFn: () => getTransaction(id as string),
    enabled: id !== undefined,
    // The app's default is one retry; a 404 is an answer, so it gets none.
    retry: (count, error) => !isMissing(error) && count < 1,
  })
  const missing = query.data === undefined && isMissing(query.error)
  const state = fromQuery(query, {
    message: (e) =>
      isMissing(e)
        ? 'This transaction no longer exists. It may have been removed by your bank.'
        : 'This transaction could not be loaded.',
    ...(missing ? {} : { recovery: { label: 'Try again', onAct: () => void query.refetch() } }),
  })
  return { ...state, missing }
}

/** One transaction as a lookup can see it. `missing` is the 404 (see `useTransaction`). */
export type TxnLookup =
  | { status: 'loading' }
  | { status: 'found'; txn: Txn }
  | { status: 'missing' }
  | { status: 'unavailable'; retry: () => void }

/**
 * Several transactions, each read BY ID through `useTransaction`'s detail key, so a row read here and
 * the detail screen share one cache entry. For a list whose rows name transactions without carrying
 * them (a rule's log). Repeated ids are read once. Returns `lookup(id)`; an id that was not asked
 * about reads as loading.
 */
export function useTransactionLookup(ids: readonly string[]): (id: string) => TxnLookup {
  const unique = [...new Set(ids)]
  const results = useQueries({
    queries: unique.map((id) => ({
      queryKey: keys.transactions.detail(id),
      queryFn: () => getTransaction(id),
      retry: (count: number, error: unknown) => !isMissing(error) && count < 1,
    })),
  })
  const byId = new Map(unique.map((id, i) => [id, results[i]]))
  return (id: string): TxnLookup => {
    const r = byId.get(id)
    if (r?.data !== undefined) return { status: 'found', txn: r.data }
    if (r?.isError) {
      if (isMissing(r.error)) return { status: 'missing' }
      const { refetch } = r
      return { status: 'unavailable', retry: () => void refetch() }
    }
    return { status: 'loading' }
  }
}
